// Data management utilities for JSON-based hotel data

const DATA_URL = '/data/hotel-data.json';

/**
 * Fetch hotel data from JSON file
 */
export const fetchHotelData = async () => {
  try {
    const response = await fetch(DATA_URL + '?t=' + Date.now()); // Cache busting
    if (!response.ok) {
      throw new Error('Failed to fetch hotel data');
    }
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching hotel data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update hotel data - In production, this would call an API endpoint
 * For now, we'll simulate the update and store in localStorage for persistence
 */
export const updateHotelData = async (newData) => {
  try {
    // Add timestamp
    const updatedData = {
      ...newData,
      lastUpdated: new Date().toISOString()
    };

    // In a real application, this would send to a backend API
    // For development, we'll store in localStorage
    localStorage.setItem('hotel-data-updates', JSON.stringify(updatedData));
    
    console.log('Data updated successfully (stored locally for development)');
    return { success: true, data: updatedData };
  } catch (error) {
    console.error('Error updating hotel data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get local updates if they exist, otherwise fetch from JSON
 * For admin interface, we always check JSON first to get latest data
 */
export const getHotelData = async (forceRefresh = false) => {
  try {
    // For admin or forced refresh, always get fresh data from JSON
    if (forceRefresh || window.location.pathname.includes('/qh-admin')) {
      const freshData = await fetchHotelData();
      if (freshData.success) {
        // Update localStorage with fresh data
        localStorage.setItem('hotel-data-updates', JSON.stringify(freshData.data));
        return freshData;
      }
    }

    // Check for local updates first for regular pages
    const localUpdates = localStorage.getItem('hotel-data-updates');
    if (localUpdates) {
      const parsedData = JSON.parse(localUpdates);
      return { success: true, data: parsedData, source: 'local' };
    }

    // Fall back to JSON file
    return await fetchHotelData();
  } catch (error) {
    console.error('Error getting hotel data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update specific section of hotel data
 */
export const updateDataSection = async (section, sectionData) => {
  try {
    const currentDataResult = await getHotelData();
    if (!currentDataResult.success) {
      throw new Error('Failed to get current data');
    }

    const updatedData = {
      ...currentDataResult.data,
      [section]: sectionData
    };

    return await updateHotelData(updatedData);
  } catch (error) {
    console.error(`Error updating ${section}:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Update multiple sections atomically in a single read-modify-write cycle.
 * Avoids the overwrite bug that occurs when calling updateDataSection repeatedly,
 * since each call re-fetches fresh data from the JSON file on admin pages.
 */
export const updateDataSections = async (sections) => {
  try {
    const currentDataResult = await getHotelData();
    if (!currentDataResult.success) {
      throw new Error('Failed to get current data');
    }

    const updatedData = {
      ...currentDataResult.data,
      ...sections
    };

    return await updateHotelData(updatedData);
  } catch (error) {
    console.error('Error updating data sections:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Clear local updates and revert to JSON file data
 */
export const revertToOriginal = async () => {
  try {
    localStorage.removeItem('hotel-data-updates');
    return await fetchHotelData();
  } catch (error) {
    console.error('Error reverting to original data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export current data for backup/download
 */
export const exportHotelData = async () => {
  try {
    const result = await getHotelData();
    if (!result.success) {
      throw new Error('Failed to get data for export');
    }

    const dataStr = JSON.stringify(result.data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    // Create download link
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hotel-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error exporting data:', error);
    return { success: false, error: error.message };
  }
};